import React, { useState, useCallback, useEffect } from "react";
import { Button, Spinner } from "react-bootstrap";
import {
  IconUsers,
  IconMenu2,
  IconRefresh,
  IconAlertCircle,
  IconBrandWhatsapp,
  IconArrowUpRight,
} from "@tabler/icons-react";
import { ServiceCenterProvider, useServiceCenter } from "./contexts";
import UserContext from "../../contexts/UserContext";
import {
  useWebSocket,
  useServiceActions,
  useServiceCenterData,
  useConversations,
  useMessages,
} from "./hooks";
import {
  ConversationSidebar,
  ChatWindow,
  ContactPanel,
  TransferModal,
  FinishServiceModal,
  ServiceQueueModal,
  ServiceStatsModal,
} from "./components";
import OperatorNameBadge from "./components/OperatorNameBadge";
import SoundToggleButton from "./components/SoundToggleButton";
import { contactToConversation } from "./utils/conversationAdapter";
import "./ServiceCenter.css";

/**
 * Conteudo interno do ServiceCenter (dentro do Provider)
 */
const ServiceCenterContent = () => {
  // States
  const [showContactPanel, setShowContactPanel] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showFinishModal, setShowFinishModal] = useState(false);
  const [showQueueModal, setShowQueueModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Context
  const {
    selectedConversation,
    loading,
    conversations,
    setSelectedConversation,
  } = useServiceCenter();
  const { getUserPlan } = React.useContext(UserContext);
  const isFreePlan = getUserPlan() === 6;

  // Detectar mudanças no tamanho da tela
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // No mobile, usar apenas mobileShowChat para controlar visibilidade
  // No desktop, mostrar chat sempre que houver conversa selecionada
  const shouldShowChat = isMobile
    ? mobileShowChat
    : (mobileShowChat || !!selectedConversation);

  // Hooks de dados e integracao
  const {
    accounts,
    selectedAccount,
    isInitialized,
    isLoading: isLoadingAccounts,
  } = useServiceCenterData();

  const {
    loadConversations,
    isLoading: isLoadingConversations,
  } = useConversations();

  const {
    handleIncomingMessage,
    handleMessageStatusUpdate,
  } = useMessages();

  const { transferService, finishService, startService } = useServiceActions();

  // WebSocket connection - passa callbacks para processar mensagens
  useWebSocket(handleIncomingMessage, handleMessageStatusUpdate);

  // Quando seleciona uma conversa, mostrar o chat no mobile
  // Usar useEffect para sincronizar com selectedConversation
  useEffect(() => {
    if (selectedConversation) {
      setMobileShowChat(true);
    }
  }, [selectedConversation?.id]); // Usar ID para detectar mudança de conversa

  // Handlers
  const handleBack = useCallback(() => {
    setMobileShowChat(false);
  }, []);

  const handleViewContactInfo = useCallback(() => {
    setShowContactPanel(true);
  }, []);

  const handleCloseContactPanel = useCallback(() => {
    setShowContactPanel(false);
  }, []);

  const handleRefresh = useCallback(() => {
    loadConversations();
  }, [loadConversations]);

  const handleTransfer = useCallback(
    async (data) => {
      if (selectedConversation?.contact_id) {
        await transferService(selectedConversation.contact_id, data);
        setShowTransferModal(false);
      }
    },
    [selectedConversation?.contact_id, transferService]
  );

  const handleFinish = useCallback(
    async (data) => {
      if (selectedConversation?.contact_id) {
        await finishService(selectedConversation.contact_id, data);
        setShowFinishModal(false);
      }
    },
    [selectedConversation?.contact_id, finishService]
  );

  const handleStartServiceFromQueue = useCallback(
    async (contactId) => {
      const response = await startService(contactId);
      if (!response?.success || !response.contact) return;

      // Preferir a conversa local (tem lastMessage/avatar prontos),
      // mas se não estiver carregada (fora da página inicial ou de outra
      // conta WhatsApp), montar a partir do contact do backend pra
      // garantir que o ChatWindow abra independente do provider (WAHA/Cloud API).
      const local = conversations.find((conv) => conv.contact_id === contactId);
      const conversation = local ?? contactToConversation(response.contact);

      setSelectedConversation(conversation);

      // Refresh em background pra sincronizar a lista (se foi um item
      // novo, agora aparecerá na sidebar com lastMessage/preview).
      if (!local) loadConversations();

      setShowQueueModal(false);
    },
    [startService, conversations, setSelectedConversation, loadConversations]
  );

  // Loading state inicial
  if (isLoadingAccounts || !isInitialized) {
    return (
      <div className="sc-container sc-loading">
        <div className="sc-loading__content">
          <Spinner animation="border" variant="primary" />
          <p>Carregando contas...</p>
        </div>
      </div>
    );
  }

  // Flag para verificar se há contas conectadas
  const hasNoAccounts = accounts.length === 0;

  return (
    <div className="sc-container">
      {/* Header com acoes globais */}
      <div className="sc-header">
        <div className="sc-header__left">
          <button className="sc-header__menu-btn d-md-none">
            <IconMenu2 size={22} />
          </button>
          <h1 className="sc-header__title">Central de Atendimento</h1>
        </div>

        <div className="sc-header__center">
          <OperatorNameBadge />
        </div>

        <div className="sc-header__actions">
          <SoundToggleButton />

          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleRefresh}
            disabled={isLoadingConversations || loading.conversations}
            title="Atualizar conversas"
          >
            <IconRefresh
              size={18}
              className={isLoadingConversations || loading.conversations ? "spin" : ""}
            />
          </Button>

          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setShowQueueModal(true)}
            title="Fila de atendimento"
          >
            <IconUsers size={18} />
            <span className="d-none d-md-inline ms-1">Fila</span>
          </Button>

        {/*   <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setShowStatsModal(true)}
            title="Estatisticas"
          >
            <IconChartBar size={18} />
            <span className="d-none d-md-inline ms-1">Stats</span>
          </Button> */}
        </div>
      </div>

      {/* Banner informativo quando não há contas conectadas */}
      {hasNoAccounts && (
        <div className="sc-no-account-banner">
          <div className="sc-no-account-banner__content">
            <IconAlertCircle size={20} />
            {isFreePlan ? (
              <>
                <span className="sc-no-account-banner__text">
                  <strong>Faça um upgrade para desbloquear este recurso.</strong>
                </span>
                <Button
                  variant="primary"
                  size="sm"
                  href="/my-plan"
                  className="sc-no-account-banner__btn"
                >
                  <IconArrowUpRight size={16} color="#fff" />
                  <span>Ver Planos</span>
                </Button>
              </>
            ) : (
              <>
                <span className="sc-no-account-banner__text">
                  <strong>Nenhuma conta WhatsApp conectada.</strong>
                  <span className="sc-no-account-banner__text--mobile"> Conecte para usar.</span>
                  <span className="sc-no-account-banner__text--desktop"> Conecte uma conta para começar a atender seus clientes.</span>
                </span>
                <Button
                  variant="success"
                  size="sm"
                  href="/agents"
                  className="sc-no-account-banner__btn"
                >
                  <IconBrandWhatsapp size={16} color="#fff" />
                  <span>Conectar WhatsApp</span>
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Main content */}
      <div className={`sc-main ${hasNoAccounts ? "sc-main--disabled" : ""}`}>
        {/* Sidebar */}
        <div
          className={`sc-main__sidebar ${shouldShowChat ? "hidden-mobile" : ""}`}
        >
          <ConversationSidebar showGrouped />
        </div>

        {/* Chat - SEMPRE mostra quando há conversa selecionada */}
        <div className={`sc-main__chat ${shouldShowChat ? "active" : ""}`}>
          <ChatWindow
            onBack={handleBack}
            onViewContactInfo={handleViewContactInfo}
            onOpenTransferModal={() => setShowTransferModal(true)}
            onOpenFinishModal={() => setShowFinishModal(true)}
          />
        </div>

        {/* Contact Panel */}
        <ContactPanel isOpen={showContactPanel} onClose={handleCloseContactPanel} />
      </div>

      {/* Modais */}
      <TransferModal
        show={showTransferModal}
        onHide={() => setShowTransferModal(false)}
        onTransfer={handleTransfer}
        conversation={selectedConversation}
      />

      <FinishServiceModal
        show={showFinishModal}
        onHide={() => setShowFinishModal(false)}
        onFinish={handleFinish}
        conversation={selectedConversation}
      />

      <ServiceQueueModal
        show={showQueueModal}
        onHide={() => setShowQueueModal(false)}
        onStartService={handleStartServiceFromQueue}
      />

      <ServiceStatsModal show={showStatsModal} onHide={() => setShowStatsModal(false)} />
    </div>
  );
};

/**
 * Componente principal ServiceCenter
 */
const ServiceCenter = () => {
  return (
    <ServiceCenterProvider>
      <ServiceCenterContent />
    </ServiceCenterProvider>
  );
};

export default ServiceCenter;
